import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  BuildingStorefrontIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

import ScaleBox from 'components/ScaleBox';
import CategoryList from 'components/CategoryList';
import normalizeEventDate from 'utils/normalizeEventDate';

const CARD_BASE_WIDTH = 560;
const CARD_BASE_HEIGHT = 316;

const getEventWindow = (dateObj) => {
  if (!dateObj || !dateObj.type) return { start: null, end: null };

  if (dateObj.type === 'fixed') {
    const start = moment(
      `${dateObj.startDate} ${dateObj.openHours || '00:00'}`,
      'YYYY-MM-DD HH:mm'
    );
    const end = moment(
      `${dateObj.endDate} ${dateObj.closeHours || '23:59'}`,
      'YYYY-MM-DD HH:mm'
    );
    return { start, end };
  }

  if (
    dateObj.type === 'segmented' &&
    Array.isArray(dateObj.days) &&
    dateObj.days.length
  ) {
    const sorted = [...dateObj.days]
      .filter((d) => d?.date)
      .sort((a, b) => moment(a.date).diff(moment(b.date)));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    const start = moment(
      `${first.date} ${first.openHours || '00:00'}`,
      'YYYY-MM-DD HH:mm'
    );
    const end = moment(
      `${last.date} ${last.closeHours || '23:59'}`,
      'YYYY-MM-DD HH:mm'
    );
    return { start, end };
  }

  return { start: null, end: null };
};

const formatBookingDateRange = (date, locale) => {
  if (!date || !date.type) return '';

  if (date.type === 'fixed') {
    const start = moment(date.startDate);
    const end = moment(date.endDate);
    if (!start.isValid() || !end.isValid()) return '';

    const sameMonthYear =
      start.year() === end.year() && start.month() === end.month();
    const sameYear = start.year() === end.year();

    if (sameMonthYear) {
      return `${start.locale(locale).format('D')}–${end.locale(locale).format('D MMM YYYY')}`;
    }
    if (sameYear) {
      return `${start.locale(locale).format('D MMM')} – ${end.locale(locale).format('D MMM YYYY')}`;
    }
    return `${start.locale(locale).format('D MMM YYYY')} – ${end.locale(locale).format('D MMM YYYY')}`;
  }

  if (
    date.type === 'segmented' &&
    Array.isArray(date.days) &&
    date.days.length
  ) {
    const sorted = [...date.days]
      .filter((d) => moment(d?.date).isValid())
      .sort((a, b) => moment(a.date).diff(moment(b.date)));
    if (!sorted.length) return '';

    const first = moment(sorted[0].date);
    const last = moment(sorted[sorted.length - 1].date);
    const sameMonthYear =
      first.year() === last.year() && first.month() === last.month();
    const sameYear = first.year() === last.year();

    if (sameMonthYear) {
      return `${first.locale(locale).format('D')}–${last.locale(locale).format('D MMM YYYY')}`;
    }
    if (sameYear) {
      return `${first.locale(locale).format('D MMM')} – ${last.locale(locale).format('D MMM YYYY')}`;
    }
    return `${first.locale(locale).format('D MMM YYYY')} – ${last.locale(locale).format('D MMM YYYY')}`;
  }

  return '';
};

const trimOrEmpty = (v) => (typeof v === 'string' ? v.trim() : v || '');
const getCardLocationLabel = (ev) => {
  const city = trimOrEmpty(ev?.city);
  const country = trimOrEmpty(ev?.country) || trimOrEmpty(ev?.countryCode);
  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  if (country) return country;
  return trimOrEmpty(ev?.location);
};

const getMacroCategories = (input) => {
  const macroIds = [];
  const addMacro = (macro) => {
    if (typeof macro !== 'number' || macroIds.includes(macro)) return;
    macroIds.push(macro);
  };

  if (Array.isArray(input)) {
    input.forEach((x) => addMacro(x?.macroCategory ?? x?.macroCategoryId));
  } else if (input && typeof input === 'object') {
    (Array.isArray(input.macros) ? input.macros : []).forEach(addMacro);
  }

  return macroIds.map((macroCategory) => ({
    macroCategory,
    microCategory: [],
  }));
};

const formatEuro = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  return `€${num.toFixed(num % 1 === 0 ? 0 : 2)}`;
};

const formatSize = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  return /m²?$/i.test(str) ? str : `${str} m²`;
};

const STATUS_THEMES = {
  Confirmed: {
    pillBg: 'bg-emerald-50 ring-1 ring-emerald-200',
    pillText: 'text-emerald-700',
    pillDot: 'bg-emerald-500',
  },
  PayNow: {
    pillBg: 'bg-blue-50 ring-1 ring-blue-200',
    pillText: 'text-blue-700',
    pillDot: 'bg-blue-500',
  },
  Awaiting: {
    pillBg: 'bg-yellow-50 ring-1 ring-yellow-200',
    pillText: 'text-yellow-700',
    pillDot: 'bg-yellow-400',
  },
  Rejected: {
    pillBg: 'bg-pink-50 ring-1 ring-pink-200',
    pillText: 'text-pink-700',
    pillDot: 'bg-pink-500',
  },
  Expired: {
    pillBg: 'bg-gray-100 ring-1 ring-gray-200',
    pillText: 'text-gray-600',
    pillDot: 'bg-gray-400',
  },
  Neutral: {
    pillBg: 'bg-gray-100 ring-1 ring-gray-200',
    pillText: 'text-gray-600',
    pillDot: 'bg-gray-400',
  },
};

const resolveBookingState = (booking) => {
  const status =
    booking?.effectiveStatus ||
    booking?.effectiveBookingStatus ||
    booking?.bookingStatus ||
    booking?.status;
  const rawStatus = booking?.rawStatus || booking?.databaseStatus || status;
  const paymentStatus = String(booking?.paymentStatus || '').toLowerCase();
  const hasExpired =
    booking?.hasBookingExpired === true ||
    String(status || '').toLowerCase() === 'expired';
  const isPaid = status === 'Paid' || paymentStatus === 'paid';

  if (hasExpired) return 'Expired';
  if (status === 'Rejected' || status === 'Cancelled') return 'Rejected';
  if (isPaid) return 'Confirmed';
  if (status === 'Approved' || rawStatus === 'Approved') return 'PayNow';
  return 'Awaiting';
};

const formatCountdown = (expireAt, t) => {
  if (!expireAt) return null;
  const target = moment(expireAt);
  if (!target.isValid()) return null;
  const now = moment();
  if (target.isSameOrBefore(now)) return null;
  const hours = target.diff(now, 'hours');
  if (hours >= 1) return t('dashboard.card.in_hours', { count: hours });
  const minutes = target.diff(now, 'minutes');
  return t('dashboard.card.in_minutes', { count: Math.max(minutes, 1) });
};

const DateThumb = ({ event, monthLocale }) => {
  const normalized = normalizeEventDate(event?.date);
  const { start } = getEventWindow(normalized || {});
  const day = start ? start.locale(monthLocale).format('D') : '';
  const month = start
    ? start.locale(monthLocale).format('MMM').toUpperCase().replace('.', '')
    : '';

  return (
    <div className="relative w-[100px] h-[100px] flex-shrink-0 rounded-2xl overflow-hidden border border-gray-200">
      {event?.avatar ? (
        <img
          src={event.avatar}
          alt={event.name || 'Event'}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="absolute inset-0 bg-gray-200 grid place-items-center">
          <BuildingStorefrontIcon className="w-10 h-10 text-gray-300" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/35" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
        <span className="text-[10px] font-bold tracking-widest leading-none">
          {month}
        </span>
        <span className="text-3xl font-bold leading-none mt-0.5">{day}</span>
      </div>
    </div>
  );
};

const MyBookingCard = ({ event, onManageClick }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [showAllCategories, setShowAllCategories] = useState(false);

  if (!event) return null;

  const booking = event.booking || null;
  const state = resolveBookingState(booking);
  const theme = STATUS_THEMES[state] || STATUS_THEMES.Neutral;

  const normalizedDate = normalizeEventDate(event.date);
  const { start, end } = getEventWindow(normalizedDate || {});
  const now = moment();
  const isLive = start && end && now.isSameOrAfter(start) && now.isBefore(end);
  const isEnded = end && now.isSameOrAfter(end);

  const locale = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  const dateRange = formatBookingDateRange(normalizedDate, locale);
  const locationLabel = getCardLocationLabel(event);

  const promoter = event.Promoter || event.promoter || null;
  const promoterName = promoter?.username || promoter?.name || '';
  const promoterAvatar = promoter?.avatar || null;

  const space = booking?.space || null;
  const spaceName = space?.name || null;
  const spaceSize = formatSize(space?.size);
  const spacePrice = formatEuro(space?.price);

  const countdown =
    state === 'PayNow' ? formatCountdown(booking?.expireAt, t) : null;
  const showPayButton = state === 'PayNow' && !!spacePrice;
  const macroCategories = getMacroCategories(event.categoriesAllowed);
  // The pay button eats footer width, so show one fewer category icon
  const maxVisibleCategories = showPayButton ? 3 : 4;
  const visibleMacroCategories = macroCategories.slice(0, maxVisibleCategories);
  const hiddenCategoryCount = Math.max(
    macroCategories.length - maxVisibleCategories,
    0
  );

  const stateLabel = {
    Confirmed: t('dashboard.card.confirmed'),
    PayNow: t('dashboard.card.pay_now'),
    Awaiting: t('dashboard.card.awaiting_approval'),
    Rejected: t('dashboard.card.rejected'),
    Expired: t('dashboard.card.expired', 'Expired'),
    Neutral: t('dashboard.card.expired', 'Expired'),
  }[state];

  const handleNavigateToEventDetail = () => {
    if (event.slug) navigate(`/event/${event.slug}`);
  };

  const handlePay = (e) => {
    e?.stopPropagation?.();
    if (onManageClick) onManageClick(event, 'booking-status');
  };

  return (
    <div className="relative w-full">
      <ScaleBox
        baseWidth={CARD_BASE_WIDTH}
        baseHeight={CARD_BASE_HEIGHT}
        ratioClass="aspect-[560/316]"
      >
        <div className="h-full w-full p-2">
          <div
            className={`relative h-full w-full bg-white rounded-[24px] shadow-[0_2px_10px_rgba(15,23,42,0.12)] hover:shadow-[0_4px_14px_rgba(15,23,42,0.14)] transition-shadow overflow-visible flex ${
              isEnded ? 'grayscale opacity-75' : ''
            }`}
          >
          <div className="flex-1 min-w-0 px-5 py-5 flex flex-col gap-4 overflow-hidden">
            {/* Top row: thumb + main content */}
            <div className="flex gap-4 min-w-0">
              <DateThumb event={event} monthLocale={locale} />

              <div className="flex-1 min-w-0">
                {/* Status row */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-extrabold ${theme.pillBg} ${theme.pillText}`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${theme.pillDot}`}
                    />
                    {stateLabel}
                  </span>

                  {countdown && (
                    <span className="text-sm font-extrabold text-orange-600">
                      {countdown}
                    </span>
                  )}

                  {isLive && state === 'Confirmed' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-extrabold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      {t('dashboard.card.live_now')}
                    </span>
                  )}

                  {isEnded && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-extrabold bg-gray-100 text-gray-600 tracking-wider">
                      {t('dashboard.card.ended')}
                    </span>
                  )}
                </div>

                {/* Title + meta */}
                <h3
                  title={event.name}
                  className="mt-1.5 font-extrabold text-[21px] leading-tight text-gray-900 truncate"
                >
                  {event.name}
                </h3>
                {(locationLabel || dateRange) && (
                  <p className="text-sm font-semibold text-gray-500 truncate">
                    {[locationLabel, dateRange].filter(Boolean).join(' · ')}
                  </p>
                )}

                {/* Promoter line */}
                {(promoterName || promoterAvatar) && (
                  <div className="mt-1 flex items-center gap-1.5">
                    {promoterAvatar ? (
                      <img
                        src={promoterAvatar}
                        alt={promoterName}
                        className="w-7 h-7 rounded-full object-cover"
                      />
                    ) : (
                      <UserCircleIcon className="w-7 h-7 text-gray-300" />
                    )}
                    <p className="text-sm text-gray-600">
                      <span className="text-gray-400">
                        {t('dashboard.card.by')}
                      </span>{' '}
                      <span className="font-semibold text-gray-700">
                        {promoterName || '—'}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Space details */}
            {(spaceName || spaceSize || spacePrice) && (
              <div className="grid grid-cols-3 gap-3 rounded-2xl bg-gray-50 border border-gray-100 px-5 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                <div className="min-w-0">
                  <p className="text-[11px] leading-none font-extrabold tracking-[0.16em] text-gray-400">
                    {t('dashboard.card.space')}
                  </p>
                  <p className="mt-2 text-lg font-extrabold text-gray-900 leading-none truncate">
                    {spaceName || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] leading-none font-extrabold tracking-[0.16em] text-gray-400">
                    {t('dashboard.card.size')}
                  </p>
                  <p className="mt-2 text-lg font-extrabold text-gray-900 leading-none">
                    {spaceSize || '—'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] leading-none font-extrabold tracking-[0.16em] text-gray-400">
                    {t('dashboard.card.price')}
                  </p>
                  <p className="mt-2 text-lg font-extrabold text-gray-900 leading-none">
                    {spacePrice || '—'}
                  </p>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-auto flex items-center justify-between gap-3">
              <div className="relative flex-1 min-w-0 flex items-center gap-1.5 overflow-visible">
                {!!visibleMacroCategories.length && (
                  <CategoryList
                    categories={visibleMacroCategories}
                    onlyMacro
                    withLabel
                    tooltipPlacement="top"
                    containerClass="flex items-center gap-1.5 flex-nowrap overflow-visible [&>div]:grid [&>div]:h-10 [&>div]:w-10 [&>div]:place-items-center [&>div]:rounded-xl [&>div]:bg-gray-50 [&>div]:shadow-sm"
                    macroIconClass="w-6 h-6"
                  />
                )}
                {hiddenCategoryCount > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAllCategories((prev) => !prev);
                    }}
                    className={`h-10 px-2 rounded-full text-sm font-bold transition-colors ${
                      showAllCategories
                        ? 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-300'
                        : 'text-gray-500 hover:text-gray-800'
                    }`}
                    aria-expanded={showAllCategories}
                  >
                    +{hiddenCategoryCount}
                  </button>
                )}
                {showAllCategories && hiddenCategoryCount > 0 && (
                  <div className="absolute left-0 bottom-12 z-20 max-w-[300px] rounded-2xl bg-white p-2.5 shadow-xl ring-1 ring-gray-200">
                    <CategoryList
                      categories={macroCategories}
                      onlyMacro
                      withLabel
                      tooltipPlacement="top"
                      containerClass="flex items-center gap-2 flex-wrap overflow-visible [&>div]:grid [&>div]:h-10 [&>div]:w-10 [&>div]:place-items-center [&>div]:rounded-xl [&>div]:bg-gray-50 [&>div]:shadow-sm"
                      macroIconClass="w-6 h-6"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {showPayButton && (
                  <button
                    type="button"
                    onClick={handlePay}
                    className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-extrabold text-gray-900 bg-yellow-400 hover:bg-yellow-500 shadow-sm"
                  >
                    {t('dashboard.card.pay_now')} {spacePrice}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleNavigateToEventDetail}
                  className="whitespace-nowrap px-5 py-2 rounded-full text-sm font-bold text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50"
                >
                  {t('common.view')}
                </button>
                <button
                  type="button"
                  onClick={() => onManageClick && onManageClick(event)}
                  className="whitespace-nowrap px-5 py-2 rounded-full text-sm font-extrabold text-gray-900 bg-yellow-400 hover:bg-yellow-500 shadow-sm"
                >
                  {t('common.manage')}
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </ScaleBox>
    </div>
  );
};

export default MyBookingCard;
